import api from './api';

class PaymentService {
  /**
   * Create payment for checkout
   * @param {Object} paymentData - Payment data including user info and basket
   * @returns {Promise} Payment response with token and payment URL
   */
  async createPayment(paymentData) {
    try {
      const response = await api.post('/store/payments/create', paymentData);
      return response.data;
    } catch (error) {
      console.error('Payment creation error:', error);
      throw error;
    }
  }

  /**
   * Calculate shipping fee based on cart total
   * @param {number} cartTotal - Total cart amount
   * @returns {number} Shipping fee amount
   */
  calculateShippingFee(cartTotal) {
    return cartTotal >= 1000 ? 0 : 125;
  }

  /**
   * Validate coupon code and calculate discount
   * @param {string} couponCode - Coupon code to validate
   * @param {number} cartTotal - Total cart amount
   * @returns {Promise} Coupon validation response
   */
  async validateCoupon(couponCode, cartTotal) {
    try {
      const response = await api.post('/store/payments/validate-coupon', {
        coupon_code: couponCode,
        cart_total: cartTotal
      });
      return response.data;
    } catch (error) {
      console.error('Error validating coupon:', error);
      throw error;
    }
  }

  /**
   * Prepare user basket data from cart items
   * @param {Array} cartItems - Cart items from Redux store
   * @returns {Array} Formatted user basket
   */
  prepareUserBasket(cartItems) {
    return cartItems.map(item => {
      // Handle size separately as its own field
      let size = null;
      if (item.selectedProductSize) {
        const sizeAsInteger = parseInt(item.selectedProductSize, 10);
        if (!isNaN(sizeAsInteger)) {
          size = sizeAsInteger;
        } else {
          // If it's not a number, keep as string (fallback)
          size = item.selectedProductSize;
        }
      }
      
      // Handle chain color
      let chain_color = null;
      if (item.selectedProductColor) {
        chain_color = item.selectedProductColor;
      }
      
      // Handle chain type
      let chain_type = null;
      if (item.selectedChainType) {
        chain_type = item.selectedChainType;
      }
      
      // Handle technologies (convert to array)
      let technologies = null;
      if (item.selectedTechnology) {
        // If selectedTechnology is a string, convert to array
        technologies = Array.isArray(item.selectedTechnology) 
          ? item.selectedTechnology 
          : [item.selectedTechnology];
      } else if (item.technologies && Array.isArray(item.technologies) && item.technologies.length > 0) {
        // Fallback to product's technologies array if no selection
        technologies = item.technologies;
      }
      
      // Build extras array for additional information
      const extras = [];
      if (item.extras && Array.isArray(item.extras)) {
        const convertedExtras = item.extras.map(extra => {
          const asInteger = parseInt(extra, 10);
          return !isNaN(asInteger) ? asInteger : extra;
        });
        extras.push(...convertedExtras);
      }

      const basketItem = {
        quantity: item.quantity,
        slug: item.sku || item.slug || item.id || item.name?.toLowerCase().replace(/\s+/g, '-'),
        size: size,
        chain_color: chain_color,
        chain_type: chain_type,
        technologies: technologies,
        extras: extras.length > 0 ? extras : undefined // Only include if not empty
      };
      
      // Remove undefined/null fields to keep the payload clean
      Object.keys(basketItem).forEach(key => {
        if (basketItem[key] === null || basketItem[key] === undefined) {
          delete basketItem[key];
        }
      });
      
      
      return basketItem;
    });
  }
}

export default new PaymentService();
